import { useState, useEffect, useRef, KeyboardEvent, ClipboardEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

type Step = 'email' | 'otp';

// Passkey icon component
const PasskeyIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
);

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    isLoading,
    error,
    clearError,
    isAuthenticated,
    needsOnboarding,
    passwordlessState,
    signIn,
    signInWithEmail,
    verifyCode,
    clearPasswordlessState,
  } = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [localError, setLocalError] = useState<string | null>(null);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      if (needsOnboarding) {
        navigate('/onboarding', { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, needsOnboarding, navigate]);

  // Handle URL params (errors, logged_out)
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const loggedOutParam = searchParams.get('logged_out');

    if (errorParam) {
      setLocalError(decodeURIComponent(errorParam));
    }
    if (loggedOutParam) {
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  // Sync step with passwordless state
  useEffect(() => {
    if (passwordlessState?.authRequestId) {
      setStep('otp');
    } else {
      setStep('email');
    }
  }, [passwordlessState]);

  // Focus first input on step change
  useEffect(() => {
    if (step === 'email' && emailInputRef.current) {
      emailInputRef.current.focus();
    } else if (step === 'otp' && otpInputRefs.current[0]) {
      otpInputRefs.current[0]?.focus();
    }
  }, [step]);

  const displayError = localError || error;

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLocalError(null);
    clearError();

    try {
      await signInWithEmail(email.trim());
    } catch {
      // Error is handled by context
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);

    // Auto-focus next input
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (digit && index === 5) {
      const code = newDigits.join('');
      if (code.length === 6) {
        handleVerifyCode(code);
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newDigits = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtpDigits(newDigits);

      // Focus appropriate input
      const focusIndex = Math.min(pastedData.length, 5);
      otpInputRefs.current[focusIndex]?.focus();

      // Auto-submit if full code pasted
      if (pastedData.length === 6) {
        handleVerifyCode(pastedData);
      }
    }
  };

  const handleVerifyCode = async (code: string) => {
    setLocalError(null);
    clearError();

    try {
      await verifyCode(code);
    } catch {
      // Error is handled by context
      setOtpDigits(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    }
  };

  const handleBackToEmail = () => {
    clearPasswordlessState();
    setOtpDigits(['', '', '', '', '', '']);
    setLocalError(null);
    clearError();
    setStep('email');
  };

  const handleResendCode = async () => {
    if (!passwordlessState?.email) return;

    setLocalError(null);
    clearError();
    setOtpDigits(['', '', '', '', '', '']);

    try {
      await signInWithEmail(passwordlessState.email);
    } catch {
      // Error is handled by context
    }
  };

  // Loading state for already authenticated users
  if (isAuthenticated && !displayError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mb-4" />
        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest animate-pulse">
          Redirecting...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[440px] relative">
      {/* Card Container */}
      <div className="bg-background-panel/80 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl rounded-[2.5rem] p-8 md:p-10 relative overflow-hidden">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center text-4xl mb-6 shadow-inner border border-amber-500/20">
            🐝
          </div>
          <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white mb-2">
            SmartBeez
          </h1>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 dark:text-white">
            Next-Gen Agent Workspace
          </p>
        </div>

        {/* Error Message */}
        {displayError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
            <p className="text-xs font-bold text-red-500">{displayError}</p>
          </div>
        )}

        {/* Email Step */}
        {step === 'email' && (
          <form onSubmit={handleEmailSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-white mb-2"
              >
                Email Address
              </label>
              <input
                ref={emailInputRef}
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full h-14 px-5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !email.trim()}
              className="w-full h-14 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white disabled:text-slate-500 rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-amber-500/20 disabled:shadow-none transition-all hover:scale-[1.02] active:scale-[0.98] disabled:hover:scale-100 flex items-center justify-center gap-3"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Continue with Email
                </>
              )}
            </button>

            <p className="text-center text-[10px] text-slate-500 dark:text-white uppercase tracking-widest">
              We'll send you a verification code
            </p>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              <span className="text-[10px] font-bold text-slate-500 dark:text-white uppercase tracking-widest">or</span>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
            </div>

            {/* Passkey login option */}
            <button
              type="button"
              onClick={() => signIn()}
              disabled={isLoading}
              className="w-full h-12 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-white rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2.5"
            >
              <PasskeyIcon className="w-4 h-4" />
              Sign in with Passkey
            </button>

            {/* Sign up hint */}
            <p className="text-center text-xs text-slate-500 dark:text-white pt-2">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="font-bold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 underline underline-offset-2 transition-colors"
              >
                Create one
              </Link>
            </p>
          </form>
        )}

        {/* OTP Step */}
        {step === 'otp' && (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-sm text-slate-600 dark:text-white mb-1">
                Enter the 6-digit code sent to
              </p>
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                {passwordlessState?.email}
              </p>
            </div>

            {/* OTP Input Grid */}
            <div className="flex justify-center gap-2.5">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    otpInputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  disabled={isLoading}
                  className="w-12 h-14 text-center text-xl font-bold bg-slate-100 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all disabled:opacity-50"
                />
              ))}
            </div>

            {isLoading && (
              <div className="flex justify-center">
                <div className="w-6 h-6 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
              </div>
            )}

            {/* Resend and Back buttons */}
            <div className="flex flex-col items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading}
                className="text-xs font-bold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 uppercase tracking-widest disabled:opacity-50 transition-colors"
              >
                Resend Code
              </button>

              <button
                type="button"
                onClick={handleBackToEmail}
                disabled={isLoading}
                className="text-[10px] font-bold text-slate-500 dark:text-white hover:text-slate-600 dark:hover:text-slate-300 uppercase tracking-widest disabled:opacity-50 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Use a different email
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Decorative blurred blobs */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full -z-10 blur-3xl opacity-20 pointer-events-none">
        <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] bg-amber-500 rounded-full mix-blend-screen animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-20%] w-[80%] h-[80%] bg-violet-600 rounded-full mix-blend-screen animate-pulse delay-1000" />
      </div>
    </div>
  );
}
